import { ViewportScroller } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { getCartIdByUserId, ActiveCartService, CartActions } from '@spartacus/cart/base/core';
import { Cart, CartModification, MultiCartFacade, OrderEntry } from '@spartacus/cart/base/root';
import {
  InterceptorUtil,
  OccEndpointsService,
  USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
  UserIdService,
  tryNormalizeHttpError,
  WindowRef,
  LoggerService
} from '@spartacus/core';
import moment, { Moment } from 'moment';
import { Observable, forkJoin, of, throwError, combineLatest } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { DpCartModificationList } from '@app/core/types/backend/dp-cart-modification-list.interface';
import { DpCart } from '@app/core/types/view/dp-cart.interface';
import { DpOrderEntry } from '@app/core/types/view/dp-order-entry.interface';
import { DpProduct } from '@app/core/types/view/dp-product.interface';
import { DpCartApiService } from '@app-modules/cart/shared/services/cart-api.service';

@Injectable({ providedIn: 'root' })
export class DpCartService extends ActiveCartService {
  failUpdateEntry$: Observable<void>;
  readonly #logger = inject(LoggerService);
  readonly #actions$ = inject(Actions);
  readonly #viewportScroller = inject(ViewportScroller);
  readonly #cartApiService = inject(DpCartApiService);

  constructor(
    protected multiCartFacade: MultiCartFacade,
    protected userIdService: UserIdService,
    private readonly httpClient: HttpClient,
    private readonly occEndpoints: OccEndpointsService,
    windowRef: WindowRef
  ) {
    super(multiCartFacade, userIdService, windowRef);
    this.failUpdateEntry$ = this.#actions$.pipe(
      ofType(CartActions.CART_UPDATE_ENTRY_FAIL),
      tap(() => this.#viewportScroller.scrollToPosition([0, 0]))
    );
  }

  saveContractCode(cart: string, entry: number, contractCode: string): Observable<unknown> {
    return this.#cartApiService
      .saveContractCode(cart, entry, contractCode)
      .pipe(tap(() => this.multiCartFacade.reloadCart(cart)));
  }

  getActive(): Observable<DpCart> {
    return super.getActive() as Observable<DpCart>;
  }

  getEntryByProductCode(productCode: string): Observable<OrderEntry> {
    return this.getActive().pipe(
      filter((activeCart): activeCart is DpCart => !!activeCart.entries),
      take(1),
      map(({ entries }) => {
        const [activeEntry] = entries?.filter((item) => item.product?.code === productCode) ?? [];

        return activeEntry;
      })
    );
  }

  getLastEntry(productCode: string): Observable<DpOrderEntry> {
    return super.getLastEntry(productCode) as Observable<DpOrderEntry>;
  }

  getProductMaxAddingQuantity(product: DpProduct): Observable<number> {
    return this.getActive().pipe(
      map((activeCart: DpCart) => this.calculateMaxProductQuantity(product, activeCart.entries))
    );
  }

  calculateMaxProductQuantity(product: DpProduct, entries: DpOrderEntry[] = []): number {
    const stockLevel =
      product.lifecycleStatus === 'LCS40' && !!product.stockInfo
        ? (product.stockInfo?.stockLevel ?? 0)
        : Number.MAX_SAFE_INTEGER;
    const qtySum = entries
      .filter((entry) => !!entry?.product?.code && entry.product.code === product.code)
      .map((entry) => entry.quantity ?? 0)
      .reduce((acc: number, entry: number) => acc + entry, 0);

    return stockLevel - qtySum;
  }

  addBatchEntries(entries: DpOrderEntry[]): Observable<CartModification[]> {
    const addBatchEntries = (userId: string, cart?: Cart): Observable<CartModification[]> => {
      if (!cart) {
        return throwError(() => 'Cart is not defined');
      }

      const cartId = getCartIdByUserId(cart, userId);

      return this.sendAddBatchEntriesRequest(entries, cartId, userId).pipe(
        tap(() => this.multiCartFacade.reloadCart(cartId)),
        catchError((error) => {
          // reload cart in case error occurred after some items were added
          this.multiCartFacade.reloadCart(cartId);

          throw error;
        }),
        map((modificationsWrapper) => modificationsWrapper.cartModifications)
      );
    };

    return this.requireLoadedCart().pipe(
      withLatestFrom(this.userIdService.getUserId()),
      switchMap(([cartState, userId]) => addBatchEntries(userId, cartState))
    );
  }

  addEntries(orderEntries: OrderEntry[]): Observable<boolean[]> {
    return forkJoin(
      orderEntries.map((entry) => {
        if (entry.product?.code && entry.quantity) {
          return this.addEntry(entry.product.code, entry.quantity);
        }

        return throwError(() => 'Unable to add entries');
      })
    );
  }

  addEntry(productCode: string, quantity: number): Observable<boolean | never> {
    const addNewEntry = (userId: string, cart?: Cart): Observable<boolean | never> => {
      if (!cart) {
        return throwError(() => 'Cart is not defined');
      }

      try {
        this.multiCartFacade.addEntry(userId, getCartIdByUserId(cart, userId), productCode, quantity);

        return of(true);
      } catch (error: unknown) {
        return throwError(() => 'Unable to add entry');
      }
    };

    return this.requireLoadedCart().pipe(
      withLatestFrom(this.userIdService.getUserId()),
      switchMap(([cartState, userId]) => addNewEntry(userId, cartState))
    );
  }

  updateEntry(entryNumber: number, quantity: number): Observable<boolean> {
    const updateEntry = (userId: string, cartId: string): Observable<boolean> => {
      try {
        this.multiCartFacade.updateEntry(userId, cartId, entryNumber, quantity);
        return of(true);
      } catch (error: unknown) {
        return throwError(() => error);
      }
    };

    return this.activeCartId$.pipe(
      withLatestFrom(this.userIdService.getUserId()),
      switchMap(([cartId, userId]) => updateEntry(userId, cartId)),
      take(1)
    );
  }

  updateEntryRequestedDeliveryDate(
    entryNumber: number | undefined,
    date: Moment | null
  ): Observable<DpCartModificationList> {
    return combineLatest([this.userIdService.getUserId(), this.getActiveCartId()]).pipe(
      filter(([userId, cartId]) => !!userId && !!cartId),
      mergeMap(([userId, cartId]) =>
        this.#cartApiService.updateEntries(
          [
            {
              entryNumber,
              requestedDeliveryDate: this.#getExpectedDeliveryDate(date)
            }
          ],
          userId,
          cartId
        )
      )
    );
  }

  clearCurrentCart(selectedEntryNumbers: number[]): Observable<CartModification[]> {
    return this.getActive().pipe(
      withLatestFrom(this.userIdService.getUserId()),
      mergeMap(([activeCart, userId]) =>
        this.#cartApiService
          .removeSelectedEntries(selectedEntryNumbers.join(','), userId, activeCart?.code ?? '')
          .pipe(tap((_) => this.multiCartFacade.reloadCart(activeCart?.code ?? '')))
      )
    );
  }

  /**
   * Creates and sends request for adding batch entries to cart
   */
  private sendAddBatchEntriesRequest(
    entries: DpOrderEntry[],
    cartId: string,
    userId: string
  ): Observable<{
    cartModifications: CartModification[];
  }> {
    const orderEntries = entries.map((entry) => ({
      product: {
        code: entry.product?.code
      },
      quantity: entry.quantity,
      requestedDeliveryDateString: entry.requestedDeliveryDate,
      partNumber: entry.partNumber,
      openText: entry.openText,
      connectCodeString: entry.connectCode
    }));

    // TODO: refactor parameters using HttpParams class
    const url = this.occEndpoints.buildUrl('orgUsers/${userId}/carts/${cartId}/entries/?fields=FULL', {
      urlParams: { userId, cartId }
    });

    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    // TODO: Use new HttpHeaders instead
    headers = InterceptorUtil.createHeader(USE_CUSTOMER_SUPPORT_AGENT_TOKEN, true, headers);

    const reqBody = { orderEntries };

    return this.httpClient.post<{ cartModifications: CartModification[] }>(url, reqBody, { headers }).pipe(
      catchError((error) => throwError(() => tryNormalizeHttpError(error, this.#logger))),
      map((data) => ({
        cartModifications: data.cartModifications.map((item) => ({
          ...item,
          quantity: item.quantity ?? 0
        }))
      }))
    );
  }

  #getExpectedDeliveryDate(date: Moment | null): string | null {
    if (date?.date()) {
      const validDate = date?.isAfter(moment().subtract(1, 'days')) ? date : moment();
      return validDate.format('YYYY-MM-DD[T]HH:mm:ss[Z]');
    }
    return null;
  }
}
